import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import '../App.css';

const Deficiencies = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [project, setProject]             = useState(null);
  const [deficiencyPhotos, setDeficiency] = useState([]);
  const [loading, setLoading]             = useState(true);

  // ─── Load project + filter deficiency photos ──────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) setProject(projectDoc.data());

        const snap = await getDocs(collection(db, 'projects', projectId, 'photos'));
        const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        const defPhotos = all.filter(p => p.isDeficiency === true);
        setDeficiency(defPhotos);
      } catch (err) {
        console.error('Error loading deficiency photos:', err);
      }
      setLoading(false);
    };
    load();
  }, [projectId]);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const photoUrl = p => p.thumbUrl || p.fullUrl || p.url;

  const deficiencyLabel = p => {
    // Prefer explicit deficiency description fields; fall back to caption
    return p.deficiencyDescription
      || p.deficiencyNote
      || p.deficiency
      || p.notes
      || p.caption
      || 'No description provided';
  };

  const sectionLabel = p => {
    return p.lineItemLabel || p.sectionLabel || p.sectionId || null;
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />

      <div style={styles.container}>

        {/* Title row */}
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Deficiencies</h2>
            {project && (
              <p style={styles.subtitle}>
                {project.address}{project.city ? `, ${project.city}` : ''} — {project.county}
              </p>
            )}
          </div>
          <div style={styles.titleButtons}>
            <button
              style={styles.backButton}
              onClick={() => navigate(`/project/${projectId}/photos`)}
            >
              ← Report Editor
            </button>
            <button
              style={styles.electricalButton}
              onClick={() => navigate(`/project/${projectId}/electrical`)}
            >
              Electrical Form
            </button>
          </div>
        </div>

        {/* Summary badge */}
        <div style={styles.summaryBanner}>
          <span style={styles.summaryCount}>{deficiencyPhotos.length}</span>
          <span style={styles.summaryLabel}>
            {deficiencyPhotos.length === 1 ? 'deficiency' : 'deficiencies'} documented
            {deficiencyPhotos.length > 0 ? ' — review all items requiring repair below.' : '.'}
          </span>
        </div>

        {/* Photo grid */}
        <div style={styles.card}>
          {deficiencyPhotos.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>✅</div>
              <p style={styles.emptyTitle}>No deficiencies recorded</p>
              <p style={styles.emptyHint}>
                Photos flagged as deficiencies in the iOS app will appear here automatically.
              </p>
            </div>
          ) : (
            <div style={styles.photoGrid}>
              {deficiencyPhotos.map((photo, index) => (
                <div key={photo.id} style={styles.photoCard}>

                  {/* Badge */}
                  <div style={styles.badgeRow}>
                    <span style={styles.defBadge}>⚠ Deficiency</span>
                    <span style={styles.indexBadge}>#{index + 1}</span>
                  </div>

                  {/* Thumbnail */}
                  <div style={styles.thumbWrap}>
                    <img
                      src={photoUrl(photo)}
                      alt={deficiencyLabel(photo)}
                      style={styles.thumbnail}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={styles.thumbFallback}>
                      <span>Image unavailable</span>
                    </div>
                  </div>

                  {/* Section association */}
                  {sectionLabel(photo) && (
                    <div style={styles.sectionChip}>
                      {sectionLabel(photo)}
                    </div>
                  )}

                  {/* Deficiency description */}
                  <div style={styles.descriptionBox}>
                    <p style={styles.descriptionText}>{deficiencyLabel(photo)}</p>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      <Footer />
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  container:       { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },

  titleRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title:           { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:        { margin: 0, color: '#555', fontSize: '0.9rem' },
  titleButtons:    { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  backButton:      { padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },
  electricalButton:{ padding: '0.5rem 1.25rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },

  summaryBanner:   { display: 'flex', alignItems: 'center', gap: '0.6rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '6px', padding: '0.65rem 1rem', marginBottom: '1.25rem', fontSize: '0.9rem', color: '#7f1d1d' },
  summaryCount:    { fontWeight: '700', fontSize: '1.2rem', color: '#b91c1c' },
  summaryLabel:    { color: '#7f1d1d' },

  card:            { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },

  photoGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1.25rem' },

  photoCard:       { border: '1px solid #fca5a5', borderRadius: '8px', backgroundColor: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  badgeRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.6rem', backgroundColor: '#fee2e2' },
  defBadge:        { backgroundColor: '#fecaca', color: '#b91c1c', fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '3px', letterSpacing: '0.04em' },
  indexBadge:      { fontSize: '0.7rem', color: '#9ca3af', fontWeight: '600' },

  thumbWrap:       { position: 'relative', backgroundColor: '#111' },
  thumbnail:       { width: '100%', height: '220px', objectFit: 'contain', display: 'block' },
  thumbFallback:   { display: 'none', width: '100%', height: '220px', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', color: '#9ca3af', fontSize: '0.8rem' },

  sectionChip:     { margin: '0.5rem 0.6rem 0', display: 'inline-block', backgroundColor: 'rgba(100,149,237,0.12)', color: '#3b5998', fontSize: '0.7rem', fontWeight: '600', padding: '0.2rem 0.55rem', borderRadius: '12px', alignSelf: 'flex-start' },

  descriptionBox:  { padding: '0.5rem 0.6rem 0.75rem' },
  descriptionText: { margin: 0, fontSize: '0.8rem', color: '#374151', lineHeight: '1.45' },

  emptyState:      { textAlign: 'center', padding: '3rem 1rem' },
  emptyIcon:       { fontSize: '2.5rem', marginBottom: '0.75rem' },
  emptyTitle:      { fontSize: '1.1rem', fontWeight: '600', color: '#112b49', margin: '0 0 0.5rem' },
  emptyHint:       { fontSize: '0.875rem', color: '#6b7280', margin: 0 },
};

export default Deficiencies;